import { supabase } from '@/services/supabase';
import { BlogPost, BlogPostForm } from '@/types/blog';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { toast } from '../useToast';

export function useBlogQueries() {
    const queryClient = useQueryClient();

    // Fetching functions
    const fetchBlogPost = async (slug: string) => {
        const { data, error } = await supabase
            .from('blog_posts')
            .select('*')
            .eq('slug', slug)
            .single();

        if (error) throw error;
        return data as BlogPost;
    };

    const fetchBlogPosts = async (limit?: number) => {
        let query = supabase
            .from('blog_posts')
            .select('*')
            .eq('published', true)
            .order('date', { ascending: false });

        if (limit) {
            query = query.limit(limit);
        }

        const { data, error } = await query;
        if (error) throw error;
        return data as BlogPost[];
    };

    // Mutation functions
    const createBlogPost = async ({ data, userId }: { data: BlogPostForm; userId: string }) => {
        const slug = data.title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');

        const { error } = await supabase
            .from('blog_posts')
            .insert([
                {
                    ...data,
                    user_id: userId,
                    slug,
                    date: new Date().toISOString(),
                    published: true,
                }
            ]);

        if (error) throw error;
        return { slug };
    };

    const updateBlogPost = async ({ slug, data }: { slug: string; data: BlogPostForm }) => {
        const { error } = await supabase
            .from('blog_posts')
            .update({
                ...data,
                updated_at: new Date().toISOString(),
            })
            .eq('slug', slug);

        if (error) throw error;
        return { slug, data };
    };

    const deleteBlogPost = async (slug: string) => {
        const { error } = await supabase
            .from('blog_posts')
            .delete()
            .eq('slug', slug);

        if (error) throw error;
        return { slug };
    };

    return {
        // Query hooks
        useFetchBlogPost: (slug: string) => useSuspenseQuery({
            queryKey: ['blog', slug],
            queryFn: () => fetchBlogPost(slug),
        }),
        
        useFetchBlogPosts: (limit?: number) => useSuspenseQuery({
            queryKey: ['blogs', limit],
            queryFn: () => fetchBlogPosts(limit),
        }),

        // Mutation hooks
        createBlogPostMutation: useMutation({
            mutationFn: createBlogPost,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['blogs'] });
                toast({
                    title: "Success",
                    description: "Your post has been published",
                });
            },
            onError: (error) => {
                toast({
                    title: "Error",
                    description: error instanceof Error ? error.message : "Failed to publish post",
                    variant: "destructive",
                });
            },
        }),

        updateBlogPostMutation: useMutation({
            mutationFn: updateBlogPost,
            onSuccess: (data) => {
                queryClient.invalidateQueries({ queryKey: ['blog', data.slug] });
                queryClient.invalidateQueries({ queryKey: ['blogs'] });
                toast({
                    title: "Success",
                    description: "Your post has been updated",
                });
            },
            onError: (error) => {
                toast({
                    title: "Error",
                    description: error instanceof Error ? error.message : "Failed to update post",
                    variant: "destructive",
                });
            },
        }),

        deleteBlogPostMutation: useMutation({
            mutationFn: deleteBlogPost,
            onSuccess: (data) => {
                queryClient.invalidateQueries({ queryKey: ['blog', data.slug] });
                queryClient.invalidateQueries({ queryKey: ['blogs'] });
                toast({
                    title: "Success",
                    description: "Your post has been deleted",
                });
            },
            onError: (error) => {
                toast({
                    title: "Error",
                    description: error instanceof Error ? error.message : "Failed to delete post",
                    variant: "destructive",
                });
            },
        }),
    };
}